import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useGetSmDayWiseRecordsMutation } from "state/api";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  ButtonGroup,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

const SmDailyWiseRecords = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get SM's MgCd from state
  const MgCd = useSelector((state) => state.global.MgCd);

  // View mode
  const [viewMode, setViewMode] = useState("prescription");

  // Date filter states
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Data and loading states
  const [prescriptionData, setPrescriptionData] = useState([]);
  const [pobData, setPobData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [smLocation, setSmLocation] = useState("");

  // API mutation
  const [getSmDayWiseRecords] = useGetSmDayWiseRecordsMutation();

  // Fetch data
  const handleFetchData = async () => {
    if (!MgCd) {
      setError("SM location code (MgCd) not found. Please login again.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log("Fetching SM records with:", {
        SmMgCd: MgCd,
        startDate,
        endDate,
      });

      const response = await getSmDayWiseRecords({
        SmMgCd: MgCd,
        startDate: startDate,
        endDate: endDate,
      }).unwrap();

      console.log("Response received:", response);

      if (response.success) {
        setPrescriptionData(response.data.prescription || []);
        setPobData(response.data.pob || []);
        setSmLocation(response.data.smLocation || "");
      } else {
        setError(response.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("Full error object:", err);
      console.error("Error status:", err?.status);
      console.error("Error data:", err?.data);
      setError(
        err?.data?.message ||
          err?.data?.error ||
          "An error occurred while fetching data"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    handleFetchData();
  }, [MgCd]);

  // Export to Excel
  const handleExport = (data, fileName) => {
    if (data.length === 0) {
      alert("No data to export");
      return;
    }

    const exportData = data.map((row) => ({
      RmMgName: row.RmMgName,
      MrName: row.MrName,
      MclCode: row.MclCode,
      DoctName: row.DoctName,
      Specification: row.Specification,
      Product: row.product,
      Approved: row.Approved,
      Rejected: row.Rejected,
      Pending: row.Pending,
      Date: row.Date,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "Records");
    XLSX.writeFile(workbook, `${fileName}_Records.xlsx`);
  };

  // Table rendering logic
  const renderTable = (data, recordType) => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            p: 3,
            backgroundColor: "#fee",
            border: "1px solid #fcc",
            borderRadius: 1,
            color: "#c33",
            textAlign: "center",
          }}
        >
          <Typography variant="body1">{error}</Typography>
        </Box>
      );
    }

    if (data.length === 0) {
      return (
        <Box
          sx={{
            p: 3,
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
            borderRadius: 1,
            color: "#666",
            textAlign: "center",
          }}
        >
          <Typography variant="body1">
            No {recordType} records found for the last 7 days
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ overflow: isMobile ? "auto" : "visible", mb: 3 }}>
        <TableContainer
          component={Paper}
          sx={{
            mb: 3,
            boxShadow: 3,
            maxHeight: isMobile ? "600px" : "none",
            overflowY: isMobile ? "auto" : "visible",
          }}
        >
          <Table
            sx={{ backgroundColor: "#1e293b" }}
            size={isMobile ? "small" : "medium"}
          >
            <TableHead sx={{ backgroundColor: "#0f172a" }}>
              <TableRow>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 100 : 140,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    backgroundColor: "#0f172a",
                  }}
                >
                  RM Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 100 : 140,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                  }}
                >
                  MR Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 80 : 120,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                  }}
                >
                  MCL Code
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 100 : 140,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                  }}
                >
                  Doctor Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 80 : 120,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                  }}
                >
                  Specification
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 80 : 120,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                  }}
                >
                  Product
                </TableCell>
                <TableCell
                  sx={{
                    color: "#22c55e",
                    fontWeight: 700,
                    minWidth: isMobile ? 70 : 100,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    textAlign: "center",
                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                  }}
                >
                  Approved
                </TableCell>
                <TableCell
                  sx={{
                    color: "#ef4444",
                    fontWeight: 700,
                    minWidth: isMobile ? 70 : 100,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    textAlign: "center",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                  }}
                >
                  Rejected
                </TableCell>
                <TableCell
                  sx={{
                    color: "#f59e0b",
                    fontWeight: 700,
                    minWidth: isMobile ? 70 : 100,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    textAlign: "center",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                  }}
                >
                  Pending
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    minWidth: isMobile ? 80 : 120,
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                  }}
                >
                  Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? "#1e293b" : "#0f172a",
                    "&:hover": { backgroundColor: "#334155" },
                    borderBottom: "1px solid rgba(59, 130, 246, 0.1)",
                  }}
                >
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 100 : 140,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {isMobile ? row.RmMgName.substring(0, 10) : row.RmMgName}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 100 : 140,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {isMobile ? row.MrName.substring(0, 10) : row.MrName}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 80 : 120,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {row.MclCode || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 100 : 140,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {isMobile ? row.DoctName.substring(0, 10) : row.DoctName}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 80 : 120,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {row.Specification || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 80 : 120,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {row.product || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#22c55e",
                      minWidth: isMobile ? 70 : 100,
                      textAlign: "center",
                      backgroundColor: "rgba(34, 197, 94, 0.05)",
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      fontWeight: 600,
                    }}
                    align="center"
                  >
                    {row.Approved || 0}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#ef4444",
                      minWidth: isMobile ? 70 : 100,
                      textAlign: "center",
                      backgroundColor: "rgba(239, 68, 68, 0.05)",
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      fontWeight: 600,
                    }}
                    align="center"
                  >
                    {row.Rejected || 0}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#f59e0b",
                      minWidth: isMobile ? 70 : 100,
                      textAlign: "center",
                      backgroundColor: "rgba(245, 158, 11, 0.05)",
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      fontWeight: 600,
                    }}
                    align="center"
                  >
                    {row.Pending || 0}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#e0e7ff",
                      minWidth: isMobile ? 80 : 120,
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  >
                    {row.Date ? new Date(row.Date).toLocaleDateString() : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
        p: isMobile ? 2 : 4,
        pb: isMobile ? 4 : 8,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: isMobile ? "center" : "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: isMobile ? 2 : 4,
          pb: isMobile ? 2 : 3,
          borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
          flexWrap: "wrap",
          gap: isMobile ? 1 : 2,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Box
          sx={{
            textAlign: isMobile ? "center" : "left",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h3"}
            fontWeight={700}
            sx={{
              color: "white",
              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              mb: 1,
              fontSize: isMobile ? "1.3rem" : "1.875rem",
            }}
          >
            Daily Wise Records Dashboard
          </Typography>
          {smLocation && (
            <Typography
              variant={isMobile ? "caption" : "subtitle1"}
              sx={{
                color: "rgba(59, 130, 246, 0.9)",
                fontWeight: 600,
                fontSize: isMobile ? "0.85rem" : "1rem",
              }}
            >
              Location: {smLocation}
            </Typography>
          )}
        </Box>

        <ButtonGroup
          variant="contained"
          orientation={isMobile ? "vertical" : "horizontal"}
          fullWidth={isMobile}
          sx={{
            mb: isMobile ? 2 : 3,
            "& .MuiButton-root": {
              borderColor: "rgba(59, 130, 246, 0.3)",
              transition: "all 0.3s",
            },
          }}
        >
          <Button
            sx={{
              background:
                viewMode === "prescription"
                  ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                  : "rgba(255, 255, 255, 0.1)",
              color: "white",
              border:
                "1px solid " +
                (viewMode === "prescription"
                  ? "rgba(59, 130, 246, 0.5)"
                  : "rgba(255, 255, 255, 0.2)"),
              py: isMobile ? 1.2 : 1,
              px: isMobile ? 1.5 : 3,
              fontWeight: 600,
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": {
                background:
                  viewMode === "prescription"
                    ? "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)"
                    : "rgba(255, 255, 255, 0.15)",
              },
            }}
            onClick={() => setViewMode("prescription")}
          >
            {isMobile ? "Rx" : "Prescription"}
          </Button>
          <Button
            sx={{
              background:
                viewMode === "pob"
                  ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                  : "rgba(255, 255, 255, 0.1)",
              color: "white",
              border:
                "1px solid " +
                (viewMode === "pob"
                  ? "rgba(168, 85, 247, 0.5)"
                  : "rgba(255, 255, 255, 0.2)"),
              py: isMobile ? 1.2 : 1,
              px: isMobile ? 1.5 : 3,
              fontWeight: 600,
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": {
                background:
                  viewMode === "pob"
                    ? "linear-gradient(45deg, #a855f7 0%, #9333ea 100%)"
                    : "rgba(255, 255, 255, 0.15)",
              },
            }}
            onClick={() => setViewMode("pob")}
          >
            {isMobile ? "POB" : "POB"}
          </Button>
        </ButtonGroup>
      </Box>

      {/* Control Section - Date Filters, Refresh and Export Buttons */}
      <Card
        sx={{
          mb: isMobile ? 2 : 4,
          backgroundColor: "rgba(30, 41, 59, 0.8)",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          backdropFilter: "blur(10px)",
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 3 }}>
          {/* Date Filter Row */}
          <Grid
            container
            spacing={isMobile ? 1 : 3}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="From Date"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  // Optionally trigger fetch on date change
                  // handleFetchData();
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: { color: "rgba(255, 255, 255, 0.7)" },
                }}
                inputProps={{
                  max: endDate,
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    "& fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.8)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="To Date"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  // Optionally trigger fetch on date change
                  // handleFetchData();
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: { color: "rgba(255, 255, 255, 0.7)" },
                }}
                inputProps={{
                  min: startDate,
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    "& fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.8)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleFetchData}
                disabled={loading}
                sx={{
                  background:
                    "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)",
                  color: "white",
                  fontWeight: 600,
                  py: isMobile ? 1.2 : 1.5,
                  px: isMobile ? 1 : 2,
                  fontSize: isMobile ? "0.8rem" : "1rem",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #2563eb 0%, #1d4ed8 100%)",
                  },
                  "&:disabled": {
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {loading
                  ? isMobile
                    ? "..."
                    : "Loading..."
                  : isMobile
                  ? "Fetch"
                  : "Fetch Data"}
              </Button>
            </Grid>
          </Grid>

          {/* Action Buttons Row */}
          <Grid container spacing={isMobile ? 1 : 3} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                startIcon={isMobile ? null : <RefreshIcon />}
                onClick={handleFetchData}
                disabled={loading}
                sx={{
                  background:
                    "linear-gradient(45deg, #3b82f6 0%, #2563eb 100%)",
                  color: "white",
                  fontWeight: 600,
                  py: isMobile ? 1.2 : 1.5,
                  px: isMobile ? 1 : 2,
                  fontSize: isMobile ? "0.8rem" : "1rem",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #2563eb 0%, #1d4ed8 100%)",
                  },
                  "&:disabled": {
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {loading
                  ? isMobile
                    ? "..."
                    : "Loading..."
                  : isMobile
                  ? "Refresh"
                  : "Refresh Data"}
              </Button>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={isMobile ? null : <DownloadIcon />}
                onClick={() =>
                  handleExport(
                    viewMode === "prescription" ? prescriptionData : pobData,
                    viewMode === "prescription"
                      ? "Prescription_Records"
                      : "POB_Records"
                  )
                }
                sx={{
                  borderColor: "rgba(59, 130, 246, 0.5)",
                  color: "rgba(59, 130, 246, 0.9)",
                  fontWeight: 600,
                  py: isMobile ? 1.2 : 1.5,
                  px: isMobile ? 1 : 2,
                  fontSize: isMobile ? "0.8rem" : "1rem",
                  "&:hover": {
                    borderColor: "rgba(59, 130, 246, 0.8)",
                    background: "rgba(59, 130, 246, 0.1)",
                  },
                }}
              >
                {isMobile ? "Export" : "Export to Excel"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Display Section */}
      <Box>
        <Typography
          variant="h6"
          sx={{
            color: "rgba(255, 255, 255, 0.9)",
            fontWeight: 600,
            mb: 3,
            fontSize: isMobile ? "0.95rem" : "1.1rem",
          }}
        >
          {viewMode === "prescription"
            ? isMobile
              ? "Rx Records"
              : "Prescription Records"
            : "POB Records"}{" "}
          - Last 7 Days
        </Typography>

        {viewMode === "prescription"
          ? renderTable(prescriptionData, "prescription")
          : renderTable(pobData, "pob")}
      </Box>
    </Box>
  );
};

export default SmDailyWiseRecords;
